import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  Grid,
  Autocomplete,
  Container,
  DialogContentText,
} from "@mui/material";
import Block from "@mui/icons-material/Block";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Edit } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../../src/SharedLink";
import axiosInstance from "../../api/axiosInstance";

const ManageSubscriberClient = () => {
  const sharedLink = useSharedLink();
  const [rows, setRows] = useState([]);
  const [subscribers, setSubscribers] = useState([]); // State to hold subscribers
  const [selectedSubscriber, setSelectedSubscriber] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [clientDetails, setClientDetails] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editClient, setEditClient] = useState(null);
  const [showAddButton, setShowAddButton] = useState(false);
  const [subscriberDetails, setSubscriberDetails] = useState("");
  const [showInactiveClients, setShowInactiveClients] = useState(false); // State to manage checkbox
  const [subscriberId, setSubscriberId] = useState(null);
  const [snackbarDuration, setSnackbarDuration] = useState(6000); // Default duration is 6000ms

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const navigate = useNavigate();

  const fetchAllSubscribers = useCallback(() => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getallsubscribers`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        setSubscribers(response.data);
      })
      .catch((error) => {
        console.error("Error fetching subscribers:", error);
      });
  }, [sharedLink]);

  useEffect(() => {
    fetchAllSubscribers();
  }, [fetchAllSubscribers]);

  const fetchAllClients = useCallback(() => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getallclients`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        const dataWithIds = response.data.map((client, index) => ({
          ...client,
          id: client.id || `temp-id-${index}`, // Assign a temporary id if missing
        }));
        setRows(
          dataWithIds.filter((client) => showInactiveClients || client.active)
        );
      })
      .catch((error) => {
        console.error("Error fetching clients:", error);
      });
  }, [sharedLink, showInactiveClients]);

  useEffect(() => {
    fetchAllClients();
  }, [fetchAllClients, showInactiveClients]);

  const handleToggleInactiveClients = () => {
    setShowInactiveClients((prev) => !prev); // Toggle the showInactiveClients state
  };

  const handleEdit = (client) => {
    const { dateOfBirth, clientName, id, profileDetails, subscriber, active } =
      client;
    const formattedDateOfBirth = new Date(dateOfBirth)
      .toISOString()
      .split("T")[0];

    setClientDetails({
      dateOfBirth: formattedDateOfBirth,
      id,
      clientName,
      profileDetails,
      subscriber: subscriber ? { id: subscriber.id } : { id: "" }, // Ensure subscriber ID is set
      active,
    });

    setIsEditMode(true);
    setOpenEdit(true);
  };

  const handleClose = () => {
    setOpenEdit(false);
    setOpenAdd(false);
    setClientDetails({});
    setIsEditMode(false);
  };

  const updateClient = () => {
    const today = new Date().toISOString().split("T")[0];
    if (clientDetails.dateOfBirth > today) {
      showSnackbar("Date of birth cannot be in the future.", "error");
      return;
    }
  
    handleDialogOpen(() => {
      if (!clientDetails.id) {
        console.error("Client ID is undefined");
        return;
      }
  
      const payload = {
        id: clientDetails.id,
        clientName: clientDetails.clientName,
        dateOfBirth: clientDetails.dateOfBirth,
        profileDetails: clientDetails.profileDetails,
        subscriber: { id: subscriberId },
        sessionNotes: [],
        active: clientDetails.active, // Include active field
      };
  
      axiosInstance
        .post(`${sharedLink}/api/abanotes/updateclient`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        })
        .then((response) => {
          //console.log("Update successful:", response.data);
          setOpenEdit(false);
  
          // Update the state with the new client details
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === clientDetails.id ? { ...row, ...clientDetails } : row
            )
          );
  
          showSnackbar("Client updated successfully!", "success");
        })
        .catch((error) => {
          console.error("Error updating client:", error);
          showSnackbar("Error updating client", "error");
        });
    });
  };

  const addClient = (e) => {
    e.preventDefault();
    const today = new Date().toISOString().split("T")[0];
    if (clientDetails.dateOfBirth > today) {
      showSnackbar("Date of birth cannot be in the future.", "error");
      return;
    }
  
    handleDialogOpen(() => {
      // Check if clientName is provided and not empty
      if (!clientDetails.clientName || clientDetails.clientName.trim() === "") {
        console.error("Client Name is required");
        showSnackbar("Client Name is required", "error", 3000);
        return;
      }
  
      // Check if dateOfBirth is provided and valid
      let dateOfBirth = clientDetails.dateOfBirth;
      if (dateOfBirth) {
        const date = new Date(dateOfBirth);
        if (!isNaN(date)) {
          dateOfBirth = date.toISOString();
        } else {
          console.error("Invalid date value");
          showSnackbar("Please provide a valid Date of Birth", "error", 3000);
          return;
        }
      } else {
        console.error("Date of Birth is required");
        showSnackbar("Date of Birth is required", "error", 3000);
        return;
      }
      const payload = {
        clientName: clientDetails.clientName,
        dateOfBirth: new Date(clientDetails.dateOfBirth).toISOString(),
        subscriber: { id: clientDetails.subscriber.id },
        sessionNotes: [],
        profileDetails: clientDetails.profileDetails,
      };
  
      axiosInstance
        .post(`${sharedLink}/api/abanotes/addclient`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        })
        .then((response) => {
          //console.log("Add successful:", response.data);
          showSnackbar(
            response.data.message || "Client added successfully!",
            "success"
          );
          setOpenAdd(false);
          handleGo();
        })
        .catch((error) => {
          console.error("Error adding client:", error);
          showSnackbar(error.response?.data || "Something went wrong", "error");
        });
    });
  };

  const today = new Date().toISOString().split("T")[0];

  const showSnackbar = (message, severity, duration = 6000) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);

    setSnackbarDuration(duration); // Set the duration state
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleAdd = () => {
    setClientDetails({
      clientName: "",
      dateOfBirth: "",
      profileDetails: "",
      subscriber: { id: subscriberId }, // Ensure this is initialized properly
      sessionNotes: [],
    });
    setIsEditMode(false);
    setOpenAdd(true);
  };

  const handleGo = () => {
    if (!selectedSubscriber) return;
    const subscriberId = selectedSubscriber.split("-")[0];
    setSubscriberId(subscriberId);

    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getclient`,
        { subscriberid: subscriberId },
        { withCredentials: true }
      )
      .then((response) => {
        const filteredClients = response.data.filter(
          (client) => showInactiveClients || client.active
        ); // Filter clients based on the checkbox state
        setRows(filteredClients);
        setShowAddButton(true);

        // Set subscriber details to show in the non-editable field
        const subscriber = subscribers.find(
          (sub) => sub.id === parseInt(subscriberId)
        );
        if (subscriber) {
          const { email, name } = subscriber;
          setSubscriberDetails(`${name} (${email})`);
        }
      })
      .catch((error) => {
        console.error("Error fetching client details:", error);
      });
  };

  const handleDialogOpen = (action, row) => {
    setDialogAction(() => action);
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogAction(null);
    setSelectedRow(null);
  };

  const handleConfirm = () => {
    if (dialogAction && selectedRow) {
      dialogAction(selectedRow);
    }
    handleDialogClose();
  };

  const handleInactive = (row) => {
    handleDialogOpen(() => {
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/deactivateclient`,
          { id: row.id },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          //console.log("Deactivation successful:", response.data);

          // Update the state to reflect the deactivation
          setRows((prevRows) =>
            prevRows.map((r) => (r.id === row.id ? { ...r, active: false } : r))
          );

          showSnackbar("Client deactivated successfully!", "success");
        })
        .catch((error) => {
          console.error("Error deactivating client:", error);
          showSnackbar("Error deactivating client", "error");
        });
    }, row);
  };

  const handleActive = (row) => {
    handleDialogOpen(() => {
      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/activateclient`,
          { id: row.id },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          //console.log("Activation successful:", response.data);

          // Update the state to reflect the activation
          setRows((prevRows) =>
            prevRows.map((r) => (r.id === row.id ? { ...r, active: true } : r))
          );

          showSnackbar("Client activated successfully!", "success");
        })
        .catch((error) => {
          console.error("Error activating client:", error);
          showSnackbar("Error activating client", "error");
        });
    }, row);
  };

  const columns = [
    { field: "id", headerName: "Client Id", width: 300 },
    { field: "clientName", headerName: "Client Name", width: 200 },
    { field: "active", headerName: "Active", width: 300 },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      width: 200,
      valueGetter: (params) =>
        new Date(params.value).toISOString().split("T")[0],
    },
    { field: "profileDetails", headerName: "Profile Details", width: 300 },
    {
      field: "actions",
      headerName: "Edit",
      type: "actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key={`edit-${params.row.id}`}
          icon={<Edit />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        params.row.active ? (
          <GridActionsCellItem
            key={`inactive-${params.row.id}`}
            icon={<Block />} // Assuming you have an icon for "Inactive"
            label="Inactive"
            onClick={() => handleInactive(params.row)}
          />
        ) : (
          <GridActionsCellItem
            key={`active-${params.row.id}`}
            icon={<CheckCircle />} // Assuming you have an icon for "Active"
            label="Active"
            onClick={() => handleActive(params.row)}
          />
        ),
      ],
    },
  ];

  return (
    <Container>
      {/* Align the heading and the controls similarly to InvoiceAdmin */}
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          marginTop: "50px",
          flexDirection: "column",
          backdropFilter: "blur(10px)",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          style={{
            color: "black",
            alignSelf: "flex-start",
            marginBottom: "20px",
            marginTop: "150px",
            fontWeight: "bold",
          }}
        >
          Manage Client
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <Autocomplete
            options={subscribers}
            getOptionLabel={(option) => {
              const enrollmentStatus =
                option.enrollments[0]?.enrollmentStatus || "Inactive";
              return `${option.email} (${enrollmentStatus})`;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Subscriber"
                variant="outlined"
                sx={{ marginRight: "10px", minWidth: "700px" }}
              />
            )}
            value={
              subscribers.find(
                (subscriber) =>
                  `${subscriber.id}-${subscriber.email}` === selectedSubscriber
              ) || null
            }
            onChange={(event, newValue) =>
              setSelectedSubscriber(
                newValue ? `${newValue.id}-${newValue.email}` : ""
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ marginRight: "10px", minWidth: "200px" }}
          />

          <Button
            variant="contained"
            onClick={handleGo}
            sx={{
              backgroundColor: "#e9a132",
              color: "white",
              "&:hover": {
                backgroundColor: "#b3741e", // Adjust this color to match the hover color
              },
            }}
            disabled={!selectedSubscriber}
          >
            Go
          </Button>

          {/* Add Client Button */}
          <Button
            variant="contained"
            onClick={handleAdd}
            sx={{
              ml: 2, // Margin left to separate from the "Go" button
              backgroundColor: showAddButton ? "#e9a132" : "grey", // Change color based on the condition
              "&:hover": {
                backgroundColor: showAddButton ? "#c78926" : "grey", // Change hover color based on the condition
              },
            }}
            disabled={!showAddButton} // Disable the button if showAddButton is false
          >
            Add Client
          </Button>
        </Box>

        <Box sx={{ height: "calc(90vh - 260px)", width: "100%" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInactiveClients}
                onChange={handleToggleInactiveClients} // Function to toggle showInactiveClients state and refetch clients
              />
            }
            label="Show Inactive Clients"
          />

          <DataGridPro
            rows={rows}
            columns={columns}
            pageSize={5}
            getRowId={(row) => row.id}
            className="animated-input"
            sx={{
              boxShadow: 5,
              border: "none",
              "& .MuiDataGrid-cell:hover": {
                color: "primary.main",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                fontWeight: "bold !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#e9a132 !important",
                borderBottom: "white !important",
                alignContent: "center",
                alignItems: "center",
                display: "flex",
                height: "100%",
              },
              "& .MuiDataGrid-columnSeparator": {
                visibility: "hidden",
              },
              "& .MuiDataGrid-toolbarContainer": {
                color: "white !important",
                justifyContent: "space-between",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                color: "white !important",
                fontWeight: "bold !important",
                backgroundColor: "#e9a132 !important",
              },
              "& .MuiTablePagination-root": {
                color: "white !important",
              },
              "& .MuiIconButton-root": {
                color: "black !important",
              },
              "& .MuiDataGrid-selectedRowCount": {
                color: "white !important",
              },
            }}
          />
        </Box>

        {/* Edit Client Dialog */}
        <Dialog open={openEdit} onClose={handleClose}>
          <DialogTitle>Edit Client Details</DialogTitle>
          <DialogContent>
            <TextField
              label="Subscriber"
              value={subscriberDetails}
              variant="outlined"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              margin="dense"
              sx={{ mt: 2, mb: 2 }} // Adjust margins as needed
              disabled
            />
            <TextField
              label={
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Client Name
                  <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                    *
                  </Box>
                </Box>
              }
              value={clientDetails.clientName || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  clientName: e.target.value,
                })
              }
            />
            <TextField
              label={
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Date of Birth
                  <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                    *
                  </Box>
                </Box>
              }
              type="date"
              value={clientDetails.dateOfBirth || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  dateOfBirth: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split("T")[0], // Set the max attribute to today's date
              }}
            />
            <TextField
              label="Profile Details"
              value={clientDetails.profileDetails || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  profileDetails: e.target.value,
                })
              }
              // Restrict the input to a maximum of 1000 characters
              inputProps={{ maxLength: 1000 }}
            />
            {/* Add a dropdown for 'active' field */}
            <FormControl fullWidth margin="dense" variant="outlined">
              <InputLabel>Active</InputLabel>
              <Select
                value={String(clientDetails.active)} // Convert boolean to string for display
                onChange={
                  (e) =>
                    setClientDetails({
                      ...clientDetails,
                      active: e.target.value === "true",
                    }) // Convert string back to boolean for state
                }
                label="Active"
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={updateClient}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#e9a132",
                "&:hover": { backgroundColor: "#c78926" },
              }}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Client Dialog */}
        <Dialog open={openAdd} onClose={handleClose}>
          <DialogTitle>Add Client Details</DialogTitle>
          <DialogContent>
            <TextField
              label="Subscriber"
              value={subscriberDetails}
              variant="outlined"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              margin="dense"
              sx={{ mt: 2, mb: 2 }} // Adjust margins as needed
              disabled
            />
            <TextField
              label={
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Client Name
                  <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                    *
                  </Box>
                </Box>
              }
              value={clientDetails.clientName || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  clientName: e.target.value,
                })
              }
            />
            <TextField
              label={
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  Date of Birth
                  <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                    *
                  </Box>
                </Box>
              }
              type="date"
              value={clientDetails.dateOfBirth || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  dateOfBirth: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split("T")[0], // Set the max attribute to today's date
              }}
            />
            <TextField
              label="Profile Details"
              value={clientDetails.profileDetails || ""}
              fullWidth
              margin="dense"
              variant="outlined"
              onChange={(e) =>
                setClientDetails({
                  ...clientDetails,
                  profileDetails: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
            <Button
              onClick={addClient}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#e9a132",
                "&:hover": { backgroundColor: "#c78926" },
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarDuration}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to proceed with this action?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageSubscriberClient;
